/**
 * PageEditor.tsx
 * Full page editor for the Make a Page builder.
 */
import { useState } from "react";
import { slugify, type CustomPage, type PageBlock, type BlockType } from "@/utils/customPageStore";
import { BLOCK_TYPES, newBlock, cloneBlock, homeSectionTemplates } from "./pageBuilderUtils";
import BlockEditor from "./BlockEditor";
import InsertBar from "./InsertBar";

interface PageEditorProps {
  page: CustomPage;
  onSave: (p: CustomPage) => void;
  onCancel: () => void;
  isDark: boolean;
}

export default function PageEditor({ page, onSave, onCancel, isDark }: PageEditorProps) {
  const [draft, setDraft] = useState<CustomPage>({ ...page, blocks: [...page.blocks] });
  const [showBlockPicker, setShowBlockPicker] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [saved, setSaved] = useState(false);

  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const inputCls = `w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 ${
    isDark ? "border-gray-600 bg-gray-700 text-white placeholder-gray-500" : "border-gray-200 bg-white text-gray-800"
  }`;
  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";

  const handleTitleChange = (title: string) => {
    const slug = draft.id === "new" ? slugify(title) : draft.slug;
    setDraft({ ...draft, title, slug });
  };

  const handleSlugChange = (slug: string) => {
    setDraft({ ...draft, slug: slugify(slug) });
  };

  const addBlock = (type: BlockType, afterIndex?: number) => {
    const nb = newBlock(type);
    const blocks = [...draft.blocks];
    if (afterIndex !== undefined) {
      blocks.splice(afterIndex + 1, 0, nb);
    } else {
      blocks.push(nb);
    }
    setDraft({ ...draft, blocks });
    setShowBlockPicker(false);
  };

  const updateBlock = (idx: number, b: PageBlock) => {
    const blocks = draft.blocks.map((bl, i) => i === idx ? b : bl);
    setDraft({ ...draft, blocks });
  };

  const deleteBlock = (idx: number) => {
    setDraft({ ...draft, blocks: draft.blocks.filter((_, i) => i !== idx) });
  };

  const moveBlock = (idx: number, dir: -1 | 1) => {
    const blocks = [...draft.blocks];
    const target = idx + dir;
    if (target < 0 || target >= blocks.length) return;
    [blocks[idx], blocks[target]] = [blocks[target], blocks[idx]];
    setDraft({ ...draft, blocks });
  };

  const duplicateBlock = (idx: number) => {
    const blocks = [...draft.blocks];
    const cloned = cloneBlock(blocks[idx]);
    blocks.splice(idx + 1, 0, cloned);
    setDraft({ ...draft, blocks });
  };

  const importTemplate = (templateBlocks: PageBlock[]) => {
    const freshBlocks = templateBlocks.map((b) => cloneBlock(b));
    setDraft({ ...draft, blocks: [...draft.blocks, ...freshBlocks] });
    setShowTemplatePicker(false);
    setShowBlockPicker(false);
  };

  const handleSave = () => {
    const now = new Date().toISOString();
    const finalPage: CustomPage = {
      ...draft,
      id: draft.id === "new" ? `page-${Date.now()}` : draft.id,
      updatedAt: now,
      createdAt: draft.createdAt || now,
    };
    onSave(finalPage);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <button onClick={onCancel} className={`w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-colors ${isDark ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}>
            <i className="ri-arrow-left-line"></i>
          </button>
          <div>
            <h2 className={`text-lg font-bold ${headingColor}`}>
              {draft.id === "new" ? "Create New Page" : `Editing: ${draft.title}`}
            </h2>
            <p className={`text-xs ${subColor}`}>
              URL: <span className="font-mono text-pink-500">/{draft.slug || "page-url"}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {saved && (
            <span className="text-xs font-semibold text-green-600 bg-green-50 px-3 py-1.5 rounded-full flex items-center gap-1">
              <i className="ri-check-line"></i> Saved!
            </span>
          )}
          <label className="flex items-center gap-2 cursor-pointer">
            <span className={`text-xs font-semibold ${subColor}`}>Visible:</span>
            <button type="button" onClick={() => setDraft({ ...draft, visible: !draft.visible })}
              className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${draft.visible ? "bg-green-500" : isDark ? "bg-gray-600" : "bg-gray-300"}`}>
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${draft.visible ? "translate-x-5" : "translate-x-0.5"}`}></span>
            </button>
          </label>
          <button onClick={handleSave} className="bg-green-500 hover:bg-green-600 text-white text-sm font-bold px-5 py-2 rounded-full cursor-pointer whitespace-nowrap transition-colors">
            <i className="ri-save-line mr-1"></i>Save Page
          </button>
        </div>
      </div>

      {/* Page settings */}
      <div className={`rounded-2xl border p-5 ${cardBg}`}>
        <h3 className={`font-bold text-sm mb-4 ${headingColor}`}>
          <i className="ri-settings-3-line text-pink-400 mr-1.5"></i>
          Page Settings
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={`text-xs font-semibold mb-1.5 block ${subColor}`}>Page Title *</label>
            <input type="text" value={draft.title} onChange={(e) => handleTitleChange(e.target.value)} placeholder="e.g. About Us" className={inputCls} />
          </div>
          <div>
            <label className={`text-xs font-semibold mb-1.5 block ${subColor}`}>URL Slug *</label>
            <div className="flex items-center gap-1">
              <span className={`text-sm font-mono ${subColor}`}>/</span>
              <input type="text" value={draft.slug} onChange={(e) => handleSlugChange(e.target.value)} placeholder="about-us" className={inputCls} />
            </div>
            <p className={`text-xs mt-1 ${subColor}`}>Only lowercase letters, numbers, and hyphens</p>
          </div>
        </div>
      </div>

      {/* Blocks */}
      <div className={`rounded-2xl border overflow-hidden ${cardBg}`}>
        <div className={`px-5 py-4 border-b flex items-center justify-between ${isDark ? "border-gray-700" : "border-gray-100"}`}>
          <h3 className={`font-bold text-sm ${headingColor}`}>
            <i className="ri-layout-line text-pink-400 mr-1.5"></i>
            Page Content ({draft.blocks.length} blocks)
          </h3>
          <div className="flex items-center gap-2">
            <button onClick={() => { setShowTemplatePicker(!showTemplatePicker); setShowBlockPicker(false); }}
              className="flex items-center gap-1.5 text-xs font-bold border border-green-500 text-green-600 hover:bg-green-50 px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-home-4-line"></i> Import from Home
            </button>
            <button onClick={() => { setShowBlockPicker(!showBlockPicker); setShowTemplatePicker(false); }}
              className="flex items-center gap-1.5 text-xs font-bold bg-pink-500 hover:bg-pink-600 text-white px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-add-line"></i> Add Block at End
            </button>
          </div>
        </div>

        {showTemplatePicker && (
          <div className={`px-5 py-4 border-b ${isDark ? "border-gray-700 bg-green-900/10" : "border-gray-100 bg-green-50"}`}>
            <p className={`text-xs font-semibold mb-1 ${subColor}`}>Import pre-built sections from your home page — each gets brand new IDs</p>
            <p className={`text-[10px] mb-3 ${subColor}`}>Click any section below to instantly clone its content as editable blocks with completely new block IDs. No connection to the original data.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {homeSectionTemplates().map((tmpl) => (
                <button key={tmpl.id} onClick={() => importTemplate(tmpl.blocks)}
                  className={`flex items-start gap-2 px-3 py-2.5 rounded-xl border cursor-pointer transition-all text-left ${isDark ? "border-gray-600 bg-gray-700 hover:border-green-400 hover:bg-gray-600" : "border-gray-200 bg-white hover:border-green-300 hover:bg-green-50"}`}>
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-green-100 flex-shrink-0">
                    <i className={`${tmpl.icon} text-green-600 text-sm`}></i>
                  </div>
                  <div className="min-w-0">
                    <div className={`text-xs font-bold ${headingColor}`}>{tmpl.label}</div>
                    <div className={`text-[10px] ${subColor} leading-tight`}>{tmpl.description}</div>
                    <div className={`text-[10px] mt-0.5 ${subColor}`}>{tmpl.blocks.length} blocks</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {showBlockPicker && (
          <div className={`px-5 py-4 border-b ${isDark ? "border-gray-700 bg-gray-700/30" : "border-gray-100 bg-gray-50"}`}>
            <p className={`text-xs font-semibold mb-3 ${subColor}`}>Choose a block type to add at the end:</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {BLOCK_TYPES.map((bt) => (
                <button key={bt.type} onClick={() => addBlock(bt.type)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border cursor-pointer transition-all text-left ${isDark ? "border-gray-600 bg-gray-700 hover:border-pink-400 hover:bg-gray-600" : "border-gray-200 bg-white hover:border-pink-300 hover:bg-pink-50"}`}>
                  <i className={`${bt.icon} text-pink-400 text-base flex-shrink-0`}></i>
                  <div>
                    <div className={`text-xs font-bold ${headingColor}`}>{bt.label}</div>
                    <div className={`text-[10px] ${subColor}`}>{bt.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="p-5 space-y-1">
          {draft.blocks.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-3">
                <i className="ri-layout-line text-gray-400 text-xl"></i>
              </div>
              <p className={`text-sm font-semibold mb-1 ${headingColor}`}>No blocks yet</p>
              <p className={`text-xs ${subColor} mb-3`}>Click "Import from Home" to start with pre-built sections, or "Add Block" to build from scratch.</p>
              <button onClick={() => { setShowTemplatePicker(true); setShowBlockPicker(false); }}
                className="text-xs font-bold text-green-600 border border-green-500 hover:bg-green-50 px-4 py-2 rounded-full cursor-pointer whitespace-nowrap transition-colors">
                <i className="ri-home-4-line mr-1"></i>Import from Home Page
              </button>
            </div>
          ) : (
            <>
              <InsertBar onInsert={(type) => addBlock(type, -1)} isDark={isDark} />
              {draft.blocks.map((block, idx) => (
                <div key={block.id} className="space-y-1">
                  <BlockEditor
                    block={block}
                    onChange={(b) => updateBlock(idx, b)}
                    onDelete={() => deleteBlock(idx)}
                    onDuplicate={() => duplicateBlock(idx)}
                    onMoveUp={() => moveBlock(idx, -1)}
                    onMoveDown={() => moveBlock(idx, 1)}
                    isDark={isDark}
                    isFirst={idx === 0}
                    isLast={idx === draft.blocks.length - 1}
                  />
                  <InsertBar onInsert={(type) => addBlock(type, idx)} isDark={isDark} />
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      <div className="flex gap-3">
        <button onClick={handleSave} className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-full cursor-pointer whitespace-nowrap transition-colors">
          <i className="ri-save-line mr-1.5"></i>Save Page
        </button>
        <button onClick={onCancel} className={`px-6 border font-semibold py-3 rounded-full cursor-pointer whitespace-nowrap transition-colors ${isDark ? "border-gray-600 text-gray-300 hover:border-gray-400" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
          Cancel
        </button>
      </div>
    </div>
  );
}